import React, { useEffect } from 'react';
import { DownloadIcon } from 'lucide-react';
import { cities } from '../data/cityData';
import { homeFaqs } from '../data/faqs';
import { businessEntity, websiteEntity, SERVICE_CATALOG, SERVICE_AREA_CITIES, SITE_URL, BUSINESS_ID, WEBSITE_ID } from '../utils/seo';
import { SITE_OVERVIEW, ROBOTS_TXT, SITEMAP_URLS, ROUTE_TABLE, PROMO_BAR_TEXT, NAVIGATION_ITEMS, FOOTER_COLUMNS, FOOTER_BOTTOM, HOME_PAGE, SERVICE_PAGES, SHARED_SERVICE_PAGE_BLOCKS, CITY_PAGE_TEMPLATE_SECTIONS, NOT_FOUND_PAGE, AEO_COVERAGE, ExportBlock, ExportPage } from '../data/siteContentExport';
import { setNoIndex } from '../utils/seo';
/**
 * CONTENT EXPORT — a documentation view of the entire website.
 *
 * This route exists purely to produce a readable, searchable PDF of the site's
 * content and SEO metadata. It is NOT linked from the site's navigation, is
 * marked noindex, and changes nothing about the live website.
 *
 * "Download PDF" opens the browser print dialog; choosing "Save as PDF"
 * produces a text-based (searchable, selectable) PDF rather than a screenshot.
 */
export function ContentExportPage() {
  useEffect(() => {
    document.title = "Website Content Export — Mike's Pro Detail";
    setNoIndex();
  }, []);
  const exportedOn = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  return <main className="export-doc min-h-screen w-full bg-white text-neutral-900" data-id="element-580" data-mp="2dt4qg8ta7lm8">
      <style data-id="element-581" data-mp="2iubzeb75uquh">{printStyles}</style>

      {/* Toolbar (screen only) */}
      <div className="no-print sticky top-0 z-10 border-b border-neutral-200 bg-white/95 backdrop-blur-sm" data-id="element-582" data-mp="2mv4a78vol3sv">
        <div className="mx-auto flex max-w-[900px] items-center justify-between gap-4 px-6 py-4" data-id="element-583" data-mp="2ro73dfwjqb8j">
          <div data-id="element-584" data-mp="2wkntugr7x1os">
            <p className="text-sm font-semibold text-neutral-900" data-id="element-585" data-mp="2wl7alu54tk18">
              Website Content Export
            </p>
            <p className="text-xs text-neutral-500" data-id="element-586" data-mp="31j8sibqtndb4">
              Documentation snapshot — the live site is unchanged
            </p>
          </div>
          <button type="button" onClick={() => window.print()} className="inline-flex items-center gap-2 rounded-md bg-neutral-900 px-4 py-2.5 text-sm font-semibold text-white transition-colors duration-150 ease-out hover:bg-neutral-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-neutral-900 focus-visible:ring-offset-2" data-id="element-587" data-mp="2gx2hirg5szvv">
            <DownloadIcon className="h-4 w-4" aria-hidden="true" data-id="element-588" data-mp="2lt12s666y1s2" />
            Download PDF
          </button>
        </div>
      </div>

      <article className="mx-auto max-w-[900px] px-6 py-10 print:px-0 print:py-0" data-id="element-589" data-mp="2unxjdjs8qgpy">
        {/* ---------------- Cover ---------------- */}
        <header className="mb-10 border-b-2 border-neutral-900 pb-8" data-id="element-590" data-mp="39k6fwdkqyfyo">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-neutral-500" data-id="element-591" data-mp="34lbyng2fgzgo">
            Website Content &amp; SEO Export
          </p>
          <h1 className="mt-3 text-4xl font-bold leading-tight tracking-tight" data-id="element-592" data-mp="1f8rrq17dwol1">
            Mike's Pro Detail
          </h1>
          <p className="mt-2 text-lg text-neutral-600" data-id="element-593" data-mp="3lv65uuis9ynl">
            Complete content documentation of https://www.fulldetailpro.com
          </p>
          <dl className="mt-6 grid grid-cols-1 gap-x-8 gap-y-2 text-sm sm:grid-cols-2" data-id="element-594" data-mp="3qsplnyv4qjhn">
            <Field label="Exported" value={exportedOn} data-id="element-595" data-mp="3c431njuk53k3" />
            <Field label="Business" value={SITE_OVERVIEW.businessName} data-id="element-596" data-mp="3h47lnjuocgf2" />
            <Field label="Business type" value={SITE_OVERVIEW.businessType} data-id="element-597" data-mp="3h24ma97mtaxo" />
            <Field label="Phone" value={SITE_OVERVIEW.phone} data-id="element-598" data-mp="332v6hanc8iu8" />
            <Field label="Primary markets" value={SITE_OVERVIEW.primaryMarkets} data-id="element-599" data-mp="376jgdzzfzqin" />
            <Field label="Domain" value={SITE_OVERVIEW.domain} data-id="element-600" data-mp="2t68piwy4ubxh" />
            <Field label="Platform" value={SITE_OVERVIEW.framework} data-id="element-601" data-mp="2t839o91ky00i" />
            <Field label="Rendering" value={SITE_OVERVIEW.rendering} data-id="element-602" data-mp="2y6ehke8be0g3" />
            <Field label="Analytics" value={SITE_OVERVIEW.analytics} data-id="element-603" data-mp="rf5dc38lgpat" />
            <Field label="Form handler" value={SITE_OVERVIEW.formHandler} data-id="element-604" data-mp="rcsyw021ox2i" />
          </dl>
          <p className="mt-6 rounded-md border border-neutral-300 bg-neutral-50 p-4 text-sm leading-relaxed text-neutral-700" data-id="element-605" data-mp="wd73170izmv9">
            This document is a text-based export of all publicly accessible
            content and SEO metadata on the website, organized by page and
            section. All wording is reproduced exactly as it appears on the live
            site. It is intended for analysis by search, AEO, and AI-search
            tooling.
          </p>
        </header>

        {/* ---------------- Table of contents ---------------- */}
        <Section title="Table of Contents" data-id="element-606" data-mp="24ragkjw19zsm">
          <ol className="ml-5 list-decimal space-y-1 text-sm text-neutral-700" data-id="element-607" data-mp="24pfwf7sl6bpl">
            <li data-id="element-608" data-mp="1zsz7ibdkbtbg">Site architecture &amp; indexing</li>
            <li data-id="element-609" data-mp="1vr49bfqxhw1w">Global navigation, promo bar &amp; footer</li>
            <li data-id="element-610" data-mp="1vorh89rgi5b4">Business entity &amp; structured data</li>
            <li data-id="element-611" data-mp="1qs15tzqljxw1">Homepage — full content</li>
            <li data-id="element-612" data-mp="1qtu9d65wy1zt">Homepage — FAQ content (19 questions)</li>
            <li data-id="element-613" data-mp="1ltps3dx6l38j">Service pages — full content ({SERVICE_PAGES.length} pages)</li>
            <li data-id="element-614" data-mp="1b5k412dnc2q">City / service-area pages ({cities.length} pages)</li>
            <li data-id="element-615" data-mp="1d0339u38u46">404 page</li>
            <li data-id="element-616" data-mp="697bd99yihpa">Image inventory &amp; alt text</li>
            <li data-id="element-617" data-mp="67w06oe6m7o5">AI-search question coverage map</li>
          </ol>
        </Section>

        {/* ---------------- 1. Architecture ---------------- */}
        <Section title="1. Site Architecture &amp; Indexing" data-id="element-618" data-mp="fax0u9ka36kf">
          <SubHeading data-id="element-619" data-mp="f82kat4dy5wa">Route table</SubHeading>
          <Table headers={['URL path', 'Page']} rows={ROUTE_TABLE.map(r => [r.path, r.page])} data-id="element-620" data-mp="3eqbnv1rhrwmw" />

          <SubHeading data-id="element-621" data-mp="3jntzizww8pyl">robots.txt</SubHeading>
          <Pre data-id="element-622" data-mp="3jmitsujw184u">{ROBOTS_TXT}</Pre>

          <SubHeading data-id="element-623" data-mp="3nqgq6xhtw4u7">sitemap.xml — {SITEMAP_URLS.length} canonical URLs</SubHeading>
          <ul className="ml-5 list-disc space-y-0.5 text-sm text-neutral-700" data-id="element-624" data-mp="3sgx7wtsomcmd">
            {SITEMAP_URLS.map(u => <li key={u} className="break-all" data-id="element-625" data-mp="2vz102th2bzz8">
                {u}
              </li>)}
          </ul>

          <SubHeading data-id="element-626" data-mp="300e36vsrtp2g">Indexing directives</SubHeading>
          <ul className="ml-5 list-disc space-y-1 text-sm text-neutral-700" data-id="element-627" data-mp="2zz30z0ez4pdg">
            <li data-id="element-628" data-mp="75472sxemmo5">
              All public pages emit:{' '}
              <code className="rounded bg-neutral-100 px-1 py-0.5 text-xs" data-id="element-629" data-mp="272m22gf4da1">
                index, follow, max-image-preview:large, max-snippet:-1
              </code>
            </li>
            <li data-id="element-630" data-mp="gxs15fx02cj6">
              The 404 route is the only page emitting{' '}
              <code className="rounded bg-neutral-100 px-1 py-0.5 text-xs" data-id="element-631" data-mp="bxnuehlw8w65">
                noindex, follow
              </code>
              . This export page is also noindex.
            </li>
            <li data-id="element-632" data-mp="3ff5prpkhcapo">
              Canonical URLs are normalized to HTTPS + www with no trailing
              slash (except the root).
            </li>
            <li data-id="element-633" data-mp="3pc0lk0pdbx09">
              Unknown city slugs under /ventura-county/ redirect to the homepage
              rather than rendering a thin page.
            </li>
          </ul>
        </Section>

        {/* ---------------- 2. Global UI ---------------- */}
        <Section title="2. Global Navigation, Promo Bar &amp; Footer" data-id="element-634" data-mp="14l9z5t6uy6ul">
          <SubHeading data-id="element-635" data-mp="1c414w4qdgp1p">Promotional bar (top of every page)</SubHeading>
          <p className="text-sm leading-relaxed text-neutral-700" data-id="element-636" data-mp="1h4oqgavu0doj">
            {PROMO_BAR_TEXT}
          </p>

          <SubHeading data-id="element-637" data-mp="127oa13j3c55d">Main navigation</SubHeading>
          <Table headers={['Label', 'Target']} rows={NAVIGATION_ITEMS.map(n => [n.label, n.target])} data-id="element-638" data-mp="128zdt3cjdcwh" />

          <SubHeading data-id="element-639" data-mp="17829vyajg8sb">Footer</SubHeading>
          {FOOTER_COLUMNS.map(col => <div key={col.heading} className="mb-4" data-id="element-640" data-mp="1uwv5ftn90eme">
              <p className="text-sm font-semibold text-neutral-900" data-id="element-641" data-mp="1zudj1xc7ni0q">
                {col.heading}
              </p>
              <ul className="ml-5 mt-1 list-disc space-y-0.5 text-sm text-neutral-700" data-id="element-642" data-mp="1l6ioscik15lj">
                {col.links.map(l => <li key={l.label} className="break-all" data-id="element-643" data-mp="1q3sabh2tox43">
                    {l.label} — {l.target}
                  </li>)}
              </ul>
            </div>)}
          <p className="text-sm leading-relaxed text-neutral-700" data-id="element-644" data-mp="2il4myjdfig7q">
            {FOOTER_BOTTOM}
          </p>
        </Section>

        {/* ---------------- 3. Structured data ---------------- */}
        <Section title="3. Business Entity &amp; Structured Data" data-id="element-645" data-mp="ikpvy4i7fvq8">
          <p className="text-sm leading-relaxed text-neutral-700" data-id="element-646" data-mp="dqltnyjvwbvh">
            Every page emits a single JSON-LD{' '}
            <code className="rounded bg-neutral-100 px-1 py-0.5 text-xs" data-id="element-647" data-mp="9oh1l4933nay">
              @graph
            </code>{' '}
            containing the canonical business entity and website entity, plus
            page-specific nodes. All nodes reference the business by a stable{' '}
            <code className="rounded bg-neutral-100 px-1 py-0.5 text-xs" data-id="element-648" data-mp="xgntnr7s184k">
              @id
            </code>{' '}
            so search engines resolve the whole site to one business rather than
            competing duplicates.
          </p>

          <SubHeading data-id="element-649" data-mp="3d70k3f1ynesk">Entity identifiers</SubHeading>
          <Table headers={['Entity', '@id']} rows={[['LocalBusiness / AutoRepair', BUSINESS_ID], ['WebSite', WEBSITE_ID], ['Per-page WebPage / Service', `${SITE_URL}<path>#primary`], ['Per-page FAQPage', `${SITE_URL}<path>#faq`], ['Per-page BreadcrumbList', `${SITE_URL}<path>#breadcrumb`]]} data-id="element-650" data-mp="3d8v1wkhxqqsf" />

          <SubHeading data-id="element-651" data-mp="333v5oeugetcp">Canonical business entity (LocalBusiness + AutoRepair)</SubHeading>
          <Table headers={['Property', 'Value']} rows={[['name', businessEntity.name], ['telephone', businessEntity.telephone], ['url', businessEntity.url], ['priceRange', businessEntity.priceRange], ['address', 'CA, US (region only — mobile business, no storefront address)'], ['areaServed', `Ventura County, San Fernando Valley, ${SERVICE_AREA_CITIES.join(', ')}`], ['description', businessEntity.description]]} data-id="element-652" data-mp="381odfcbenvf3" />
          <p className="mt-2 text-xs leading-relaxed text-neutral-500" data-id="element-653" data-mp="3qtpiiwerwhwy">
            No aggregateRating, review, opening-hours, geo-coordinate, or award
            data is emitted, because none of it is first-party verifiable from
            the site content.
          </p>

          <SubHeading data-id="element-654" data-mp="21f1a169azk47">WebSite entity</SubHeading>
          <Table headers={['Property', 'Value']} rows={[['name', websiteEntity.name], ['url', websiteEntity.url], ['description', websiteEntity.description], ['publisher', 'References the business entity by @id'], ['inLanguage', websiteEntity.inLanguage]]} data-id="element-655" data-mp="21fueg0kshxj6" />

          <SubHeading data-id="element-656" data-mp="2440tzecyei1s">Service catalog (hasOfferCatalog)</SubHeading>
          <Table headers={['Service', 'URL', 'Description']} rows={SERVICE_CATALOG.map(s => [s.name, s.path, s.description])} data-id="element-657" data-mp="294ey4lbfp7uj" />
        </Section>

        {/* ---------------- 4. Homepage ---------------- */}
        <Section title="4. Homepage — Full Content" data-id="element-658" data-mp="2j19ryqwrik2h">
          <PageMeta page={HOME_PAGE} data-id="element-659" data-mp="2izqaiwzde2z4" />
          {HOME_PAGE.sections.map(s => <SectionBlock key={s.heading} heading={s.heading} headingLevel={s.headingLevel} blocks={s.blocks} data-id="element-660" data-mp="2ntbd72bwrl4n" />)}
        </Section>

        {/* ---------------- 5. Homepage FAQ ---------------- */}
        <Section title="5. Homepage FAQ — 19 Questions" data-id="element-661" data-mp="2wusswjlelkst">
          <p className="mb-4 text-sm leading-relaxed text-neutral-700" data-id="element-662" data-mp="31sazi3by7nxn">
            These are rendered as a visible accordion on the homepage and
            emitted as FAQPage structured data from the same source, so schema
            and visible content match exactly.
          </p>
          <FaqList faqs={homeFaqs} data-id="element-663" data-mp="31ybpex2501tj" />
        </Section>

        {/* ---------------- 6. Service pages ---------------- */}
        <Section title={`6. Service Pages — ${SERVICE_PAGES.length} Pages`} data-id="element-664" data-mp="2xx86g1oslpo3">
          <p className="mb-4 text-sm leading-relaxed text-neutral-700" data-id="element-665" data-mp="3umzc8x2jaz97">
            Every service page also renders two shared blocks:{' '}
            <strong data-id="element-666" data-mp="3poxmxueim3pa">"{SHARED_SERVICE_PAGE_BLOCKS.serviceAreasHeading}"</strong>{' '}
            — {SHARED_SERVICE_PAGE_BLOCKS.serviceAreasBody} — and a closing CTA:{' '}
            <strong data-id="element-667" data-mp="3gvuczw32w8ze">"{SHARED_SERVICE_PAGE_BLOCKS.ctaHeading}"</strong> —{' '}
            {SHARED_SERVICE_PAGE_BLOCKS.ctaBody} ({SHARED_SERVICE_PAGE_BLOCKS.ctaButtons}).
          </p>
          {SERVICE_PAGES.map(page => <div key={page.id} className="avoid-break mb-10 border-t border-neutral-200 pt-6" data-id="element-668" data-mp="cguksbq3qebr">
              <h3 className="mb-3 text-xl font-bold tracking-tight" data-id="element-669" data-mp="26ooln6nkps6q">
                6.{SERVICE_PAGES.indexOf(page) + 1} {page.name}
              </h3>
              <PageMeta page={page} data-id="element-670" data-mp="2bnjfr4qzr365" />
              {page.sections.map(s => <SectionBlock key={s.heading} heading={s.heading} headingLevel={s.headingLevel} blocks={s.blocks} data-id="element-671" data-mp="1wydg5fo8raxq" />)}
              {page.faqs && <>
                  <SubHeading data-id="element-672" data-mp="21w57o8kz4v1e">FAQ (visible + FAQPage schema)</SubHeading>
                  <FaqList faqs={page.faqs} data-id="element-673" data-mp="1nx5kl5d40o7c" />
                </>}
              {page.internalLinks && <>
                  <SubHeading data-id="element-674" data-mp="1sz4n6d10zh38">Internal links</SubHeading>
                  <p className="text-sm text-neutral-700" data-id="element-675" data-mp="1e1c9zriyb4s7">
                    {page.internalLinks.join(' · ')}
                  </p>
                </>}
            </div>)}
        </Section>

        {/* ---------------- 7. City pages ---------------- */}
        <Section title={`7. City / Service-Area Pages — ${cities.length} Pages`} data-id="element-676" data-mp="38ch6mbvmegy1">
          <p className="mb-4 text-sm leading-relaxed text-neutral-700" data-id="element-677" data-mp="3dd4zl33et7uw">
            Each city page follows the same structure. Shared sections across
            all city pages:
          </p>
          <ul className="mb-6 ml-5 list-disc space-y-1 text-sm text-neutral-700" data-id="element-678" data-mp="wst5sao7v9bc">
            {CITY_PAGE_TEMPLATE_SECTIONS.map(s => <li key={s} data-id="element-679" data-mp="1bi7insr048vh">{s}</li>)}
          </ul>
          <p className="mb-6 text-sm leading-relaxed text-neutral-700" data-id="element-680" data-mp="16p4jn53gg77w">
            Structured data per city page: LocalBusiness/AutoRepair + WebSite +
            a <strong data-id="element-681" data-mp="1kivseb9d3n2k">Service</strong> node ("Mobile Car Detailing in [City],
            CA") whose provider references the canonical business by @id, plus
            FAQPage and BreadcrumbList (Home / Service Areas / [City]). The city
            pages deliberately do <em data-id="element-682" data-mp="1pgxfd79ws6jb">not</em> declare a physical business
            location in each city, because the business is fully mobile.
          </p>

          {cities.map((city, i) => <div key={city.slug} className="avoid-break mb-10 border-t border-neutral-200 pt-6" data-id="element-683" data-mp="3r7ik087c3sir" data-mpi={i}>
              <h3 className="mb-3 text-xl font-bold tracking-tight" data-id="element-684" data-mp="3sooacjcjhbgq">
                7.{i + 1} {city.name}
              </h3>
              <dl className="mb-4 grid grid-cols-1 gap-x-8 gap-y-2 text-sm" data-id="element-685" data-mp="1hbovs4mibv0">
                <Field label="URL" value={`${SITE_URL}/ventura-county/${city.slug}`} data-id="element-686" data-mp="6hhdt0jidk9e" />
                <Field label="SEO title" value={`${city.title} | Mike's Pro Detail`} data-id="element-687" data-mp="bgaoymbboz6e" />
                <Field label="H1" value={city.title} data-id="element-688" data-mp="bdycups8xj19" />
                <Field label="Meta description" value={city.metaDescription} data-id="element-689" data-mp="39yzmfa73uvav" />
                <Field label="Canonical" value={`${SITE_URL}/ventura-county/${city.slug}`} data-id="element-690" data-mp="3ewhr2oe3aod2" />
                <Field label="Primary keyword" value={city.keyword} data-id="element-691" data-mp="3jvkpvr3h7ycl" />
                <Field label="Robots" value="index, follow, max-image-preview:large, max-snippet:-1" data-id="element-692" data-mp="3ju9jrkmk6eib" />
                <Field label="Open Graph" value="og:type=article, og:site_name, og:locale=en_US, og:url, og:title, og:description, og:image (logo); Twitter card = summary_large_image" data-id="element-693" data-mp="3oonhvusluzqo" />
              </dl>

              <SubHeading data-id="element-694" data-mp="1dbf3ml3079kp">Subtitle</SubHeading>
              <p className="text-sm leading-relaxed text-neutral-700" data-id="element-695" data-mp="1da42kt0y0fxa">
                {city.subtitle}
              </p>

              <SubHeading data-id="element-696" data-mp="19alfbqlnfsra">Body copy</SubHeading>
              {city.paragraphs.map((p, idx) => <p key={idx} className="mb-3 text-sm leading-relaxed text-neutral-700" data-id="element-697" data-mp="149fl1occbxfn" data-mpi={idx}>
                  {p}
                </p>)}

              <SubHeading data-id="element-698" data-mp="zdqnfqjvsqlz">Neighborhoods listed</SubHeading>
              <p className="text-sm text-neutral-700" data-id="element-699" data-mp="udkwkclfr84y">
                {city.neighborhoods.join(' · ')}
              </p>

              <SubHeading data-id="element-700" data-mp="pkjgi19ht2js">FAQ (visible + FAQPage schema)</SubHeading>
              <FaqList faqs={city.faqs} data-id="element-701" data-mp="llap331xdgre" />
            </div>)}
        </Section>

        {/* ---------------- 8. 404 ---------------- */}
        <Section title="8. 404 Page" data-id="element-702" data-mp="gmzbqicf8o4f">
          <dl className="grid grid-cols-1 gap-x-8 gap-y-2 text-sm" data-id="element-703" data-mp="bls3ki6d9mx8">
            <Field label="URL" value={NOT_FOUND_PAGE.url} data-id="element-704" data-mp="bo62x382xlal" />
            <Field label="SEO title" value={NOT_FOUND_PAGE.seoTitle} data-id="element-705" data-mp="6o06zaus1cmp" />
            <Field label="Robots" value={NOT_FOUND_PAGE.robots} data-id="element-706" data-mp="3t47j060bq3iv" />
            <Field label="H1" value={NOT_FOUND_PAGE.h1} data-id="element-707" data-mp="3t6tm9tg6lwva" />
            <Field label="Body" value={NOT_FOUND_PAGE.body} data-id="element-708" data-mp="1zh3xb3kbdds" />
            <Field label="Calls to action" value={NOT_FOUND_PAGE.ctas} data-id="element-709" data-mp="3k4c1zkk37eax" />
          </dl>
        </Section>

        {/* ---------------- 9. Images ---------------- */}
        <Section title="9. Image Inventory &amp; Alt Text" data-id="element-710" data-mp="3o5xlvpv1gsum">
          <p className="mb-4 text-sm leading-relaxed text-neutral-700" data-id="element-711" data-mp="fpkps0yj552p">
            All images live on the homepage; service and city pages are
            text-only apart from the shared header. Every image has descriptive
            alt text.
          </p>
          <Table headers={['File', 'Alt text', 'Notes']} rows={(HOME_PAGE.images ?? []).map(img => [img.src, img.alt, img.notes ?? '—'])} data-id="element-712" data-mp="6vppb68cs2te" />
        </Section>

        {/* ---------------- 10. AEO ---------------- */}
        <Section title="10. AI-Search Question Coverage Map" data-id="element-713" data-mp="13f6n0ivcda82">
          <p className="mb-4 text-sm leading-relaxed text-neutral-700" data-id="element-714" data-mp="13hjcdh3fimv5">
            Where the site answers the questions prospective customers ask
            search engines and AI assistants.
          </p>
          <Table headers={['Question', 'Answered on']} rows={AEO_COVERAGE.map(a => [a.question, a.answeredOn])} data-id="element-715" data-mp="2j4o7o5byoo64" />
        </Section>

        <footer className="mt-12 border-t border-neutral-300 pt-6 text-xs text-neutral-500" data-id="element-716" data-mp="2sw0qy8jp260j">
          <p data-id="element-717" data-mp="256pvn6l453ts">
            End of export — Mike's Pro Detail, https://www.fulldetailpro.com —
            generated {exportedOn}. Content reproduced verbatim from the live
            site.
          </p>
        </footer>
      </article>
    </main>;
}
/* ------------------------------------------------------------------ */
/* Presentational helpers                                              */
/* ------------------------------------------------------------------ */
function Section({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}) {
  return <section className="mb-12" data-id="element-718" data-mp="3lnc0e9fgsuvk">
      <h2 className="mb-4 border-b border-neutral-300 pb-2 text-2xl font-bold tracking-tight" dangerouslySetInnerHTML={{
      __html: title
    }} data-id="element-719" data-mp="1iyu7bcqw6fe1" />
      {children}
    </section>;
}
function SubHeading({
  children
}: {
  children: React.ReactNode;
}) {
  return <h4 className="mb-2 mt-5 text-xs font-bold uppercase tracking-[0.14em] text-neutral-500" data-id="element-720" data-mp="1svfcpz6tpsie">
      {children}
    </h4>;
}
function Field({
  label,
  value
}: {
  label: string;
  value: string;
}) {
  return <div className="flex flex-col" data-id="element-721" data-mp="22omhpixnj6hy">
      <dt className="text-[11px] font-semibold uppercase tracking-wider text-neutral-500" data-id="element-722" data-mp="26nlutqtje9kh">
        {label}
      </dt>
      <dd className="break-words text-sm text-neutral-800" data-id="element-723" data-mp="2bo9l2a9xymdn">{value}</dd>
    </div>;
}
function Pre({
  children
}: {
  children: React.ReactNode;
}) {
  return <pre className="overflow-x-auto whitespace-pre-wrap rounded border border-neutral-300 bg-neutral-50 p-3 text-xs leading-relaxed text-neutral-800" data-id="element-724" data-mp="2gjyo4nl66lep">
      {children}
    </pre>;
}
function Table({
  headers,
  rows
}: {
  headers: string[];
  rows: string[][];
}) {
  return <table className="w-full table-auto border-collapse text-sm" data-id="element-725" data-mp="eq3m0mst7yeu">
      <thead data-id="element-726" data-mp="9xspltxa2ur1">
        <tr data-id="element-727" data-mp="9xaiu3czoy4v">
          {headers.map(h => <th key={h} className="border-b border-neutral-300 py-2 pr-4 text-left text-[11px] font-bold uppercase tracking-wider text-neutral-500" data-id="element-728" data-mp="9vzf23jjnqdr">
              {h}
            </th>)}
        </tr>
      </thead>
      <tbody data-id="element-729" data-mp="3w3fs8jbqcce4">
        {rows.map((row, i) => <tr key={i} className="avoid-break align-top" data-id="element-730" data-mp="xnwrnwutimfx" data-mpi={i}>
            {row.map((cell, j) => <td key={j} className="border-b border-neutral-200 py-2 pr-4 leading-relaxed text-neutral-800" data-id="element-731" data-mp="sotlg933qa6d" data-mpi={j}>
                {cell}
              </td>)}
          </tr>)}
      </tbody>
    </table>;
}
function FaqList({
  faqs
}: {
  faqs: {
    question: string;
    answer: string;
  }[];
}) {
  return <dl className="space-y-3" data-id="element-732" data-mp="38fg2nmq5ms14">
      {faqs.map(f => <div key={f.question} className="avoid-break" data-id="element-733" data-mp="38ghhm2aajyo4">
          <dt className="text-sm font-semibold text-neutral-900" data-id="element-734" data-mp="33guzbeflvs8g">
            Q: {f.question}
          </dt>
          <dd className="mt-0.5 text-sm leading-relaxed text-neutral-700" data-id="element-735" data-mp="239mfqz9se0dg">
            A: {f.answer}
          </dd>
        </div>)}
    </dl>;
}
function PageMeta({
  page
}: {
  page: ExportPage;
}) {
  return <div className="avoid-break mb-5 rounded border border-neutral-300 bg-neutral-50 p-4" data-id="element-736" data-mp="1tirofghooal5">
      <dl className="grid grid-cols-1 gap-x-8 gap-y-2 text-sm" data-id="element-737" data-mp="1okq9n7raixfh">
        <Field label="URL" value={page.url} data-id="element-738" data-mp="1oi56mff9abg5" />
        <Field label="SEO title" value={page.seoTitle} data-id="element-739" data-mp="2m88i0hh1hxiy" />
        <Field label="Meta description" value={page.metaDescription} data-id="element-740" data-mp="2m5maghu5fi0q" />
        <Field label="Canonical" value={page.canonical} data-id="element-741" data-mp="2h9ezubqt5icg" />
        <Field label="Robots" value={page.robots} data-id="element-742" data-mp="2c99gdiuoz25g" />
        <Field label="H1" value={page.h1} data-id="element-743" data-mp="2c7zhwsjxfrya" />
        <Field label="Structured data" value={page.schema} data-id="element-744" data-mp="288qmzkd5ho15" />
        <Field label="Open Graph / Twitter" value={page.openGraph.map(o => `${o.label}: ${o.value}`).join(' | ')} data-id="element-745" data-mp="28aa4tfegg751" />
      </dl>
    </div>;
}
function SectionBlock({
  heading,
  headingLevel,
  blocks
}: {
  heading: string;
  headingLevel?: string;
  blocks: ExportBlock[];
}) {
  return <div className="avoid-break mb-6" data-id="element-746" data-mp="38hyr9pmj8e2s">
      <h4 className="mb-2 text-base font-bold text-neutral-900" data-id="element-747" data-mp="3chz5pa9msp9u">
        {heading}
        {headingLevel && <span className="ml-2 text-[11px] font-semibold uppercase tracking-wider text-neutral-400" data-id="element-748" data-mp="3hh27mlubefdl">
            {headingLevel}
          </span>}
      </h4>
      {blocks.map((block, i) => {
      if (block.type === 'p') {
        return <p key={i} className="mb-2 text-sm leading-relaxed text-neutral-700" data-id="element-749" data-mp="3r5tx8nbwknlc">
              {block.text}
            </p>;
      }
      if (block.type === 'h3') {
        return <p key={i} className="mb-1 mt-3 text-sm font-semibold text-neutral-900" data-id="element-750" data-mp="42fe0folzeww">
              {block.text}
            </p>;
      }
      if (block.type === 'list') {
        return <ul key={i} className="mb-2 ml-5 list-disc space-y-1 text-sm leading-relaxed text-neutral-700" data-id="element-751" data-mp="8z631sie5kug">
              {block.items.map(item => <li key={item} data-id="element-752" data-mp="2as3qt9xb9n8j">{item}</li>)}
            </ul>;
      }
      if (block.type === 'code') {
        return <Pre key={i} data-id="element-753" data-mp="3e3175nhzefb5">{block.text}</Pre>;
      }
      return <dl key={i} className="mb-2 space-y-1 text-sm" data-id="element-754" data-mp="3e5nble9ksep5" data-mpi={i}>
            {block.items.map(item => <div key={item.label} className="flex flex-wrap gap-x-2" data-id="element-755" data-mp="3960yr5xnt0gv">
                <dt className="font-semibold text-neutral-900" data-id="element-756" data-mp="3n5ade167vmiq">
                  {item.label}:
                </dt>
                <dd className="text-neutral-700" data-id="element-757" data-mp="3i4ctnk5k6kf4">{item.value}</dd>
              </div>)}
          </dl>;
    })}
    </div>;
}
const printStyles = `
  @media print {
    .no-print { display: none !important; }
    .export-doc { background: #fff !important; color: #000 !important; }
    .avoid-break { break-inside: avoid; page-break-inside: avoid; }
    h2 { break-after: avoid; page-break-after: avoid; }
    a { text-decoration: none; color: inherit; }
  }
  @page {
    margin: 16mm 14mm;
  }
`;